import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { faPrint, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UbicacionesIcon } from '@shared/components/ui/icons/warehouse.icons';
import { useTranslation } from 'react-i18next';
import { PackingStation, type PackingStationRelationsTabKey } from '../../models/PackingStation';
import { DropdownActions } from '../interaction/DropdownActions';

export interface PackingStationCardProps extends RenderItemProps<PackingStation> {
  onOpenDetail?: (station: PackingStation, tab?: PackingStationRelationsTabKey) => void;
  onOpenEdit?: (station: PackingStation) => void;
  onPrint?: (station: PackingStation, size: 'small' | 'large') => void;
  refetch?: () => void;
}

const pickNumber = (source: Record<string, any>, keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
};

export const PackingStationCard = ({
  item: station,
  onOpenDetail,
  onOpenEdit,
  onPrint,
  refetch,
}: PackingStationCardProps) => {
  const { t } = useTranslation('packingStationsModule');
  const rawStation = station as Record<string, any>;
  const printers = Array.isArray(rawStation.printers) ? rawStation.printers : [];
  const clients = Array.isArray(rawStation.clients) ? rawStation.clients : [];
  const locations = Array.isArray(rawStation.locations) ? rawStation.locations : [];

  const printersCount = pickNumber(rawStation, ['printers_count', 'printersCount']) ?? printers.length;
  const clientsCount = pickNumber(rawStation, ['clients_count', 'clientsCount']) ?? clients.length;
  const locationsCount = pickNumber(rawStation, ['locations_count', 'locationsCount']) ?? locations.length;

  return (
    <div className="border-b border-card-border py-4 transition-colors hover:bg-hover-press/30">
      {/* Header Section - Sección de encabezado */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium text-title">{station.code || '-'}</span>
          {station.active ? (
            <Badge variant="default" className="border-0 bg-bg-success text-xs text-success">
              {t('table.status.active')}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-title">
              {t('table.status.inactive')}
            </Badge>
          )}
        </div>
        <DropdownActions
          station={station}
          onOpenDetail={onOpenDetail}
          onOpenEdit={onOpenEdit}
          onPrint={onPrint}
          refetch={refetch}
        />
      </div>

      {/* Counters Section - Sección de contadores */}
      <div className="mb-3 grid grid-cols-3 gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-9 justify-start gap-2 px-2"
          onClick={() => onOpenDetail?.(station, 'printers')}
          data-testid={`mobile-open-printers-detail-${station.id}`}
        >
          <FontAwesomeIcon icon={faPrint} className="h-3.5 w-3.5 text-icons" />
          <span className="text-xs text-title">{printersCount}</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9 justify-start gap-2 px-2"
          onClick={() => onOpenDetail?.(station, 'clients')}
          data-testid={`mobile-open-clients-detail-${station.id}`}
        >
          <FontAwesomeIcon icon={faUser} className="h-3.5 w-3.5 text-icons" />
          <span className="text-xs text-title">{clientsCount}</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9 justify-start gap-2 px-2"
          onClick={() => onOpenDetail?.(station, 'locations')}
          data-testid={`mobile-open-locations-detail-${station.id}`}
        >
          <UbicacionesIcon className="h-3.5 w-3.5 text-icons" />
          <span className="text-xs text-title">{locationsCount}</span>
        </Button>
      </div>
    </div>
  );
};
